import React, { useState, useRef, SyntheticEvent } from 'react';
import cc from 'classcat';
import { IAlert, IsAlertSnoozed } from '../../../libs/alert';
import Popover from '../popover';
import Modal from '../modal';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import Tooltip from '../tooltip';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';
import {
  FormatDateTime,
  FormatDaysAndClassesLeft,
  GetCurrentTZ
} from '../../../libs/time';
import AnchorButton from '../../molecules/button/anchor';
import Button from '../../molecules/button';
import { useActiveDialogsUpdater } from '../../../contexts/active-dialogs';
import { IPatient, IsPatientSnoozed } from '../../../libs/patient';
import {
  DateInput,
  Form,
  FormData,
  FormErrors,
  FormInstance,
  RadioGroup,
  SubmitButton
} from '@lightbeamhealth/blueprint-form';
import moment from 'moment-timezone';
import {
  deserializeSnoozeFormData,
  serializeSnoozeFormData,
  getSnoozeDateFromDuration,
  getSnoozeDurationOptions,
  SnoozeFormData,
  SnoozeFormDataRaw
} from '../../../libs/event';
import { produce } from 'immer';
import './action.scss';
import { BaseResponse, PostJSON } from '../../../libs/request';
import { useTriage } from '../../../contexts/triage';
import { useMetaData } from '../../../contexts/metadata';
import { IDialog } from '../../../contexts/active-dialogs/types';

/**
 * This action kicks off the snooze
 * alert flow
 */

const validate = (values: FormData): FormErrors => {
  const errors: FormErrors = {};
  if (!values.reason.length) {
    errors.reason = 'You must select one of the reasons!';
  }
  if (!values.snoozeUntil && !(parseInt(values.duration) > 0)) {
    errors.duration = 'You must select a duration to snooze or pick a date!';
  }
  return errors;
};

interface PatientResponse extends BaseResponse {
  patient: IPatient;
  alerts?: IAlert[];
}

interface AlertsResponse extends BaseResponse {
  alerts: IAlert[];
}

interface UnsnoozeAlertParams {
  ids: IAlert['id'][];
}

interface SnoozeAlertActionProps {
  alert: IAlert;
  onSnoozed?: (snoozedUntil: string) => void;
  disabled?: boolean;
}

export const SnoozeAlertAction = ({
  alert,
  onSnoozed,
  disabled = false
}: SnoozeAlertActionProps): JSX.Element => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const addDialog = useActiveDialogsUpdater();
  const { handleToggleSnoozeAlert } = useTriage();
  const { settings: accountSettings } = useMetaData();
  const options = accountSettings?.snoozeOptions;
  let title = 'Snooze';
  let tooltipTitle = 'Snooze Alert';
  let tooltipDescription = null;
  const classNames = [];

  const initialData: SnoozeFormData = {
    ids: [alert.id],
    duration: '15-minutes',
    snoozeUntil: null,
    reason: options.reason[0],
    note: '',
    tz: GetCurrentTZ(),
    misc: ''
  };
  const formDataRef = useRef(initialData);
  const form = new FormInstance<SnoozeFormDataRaw, SnoozeFormData>(
    deserializeSnoozeFormData(initialData),
    {
      serialize: serializeSnoozeFormData,
      deserialize: deserializeSnoozeFormData
    }
  );

  if (IsAlertSnoozed(alert)) {
    const output = FormatDaysAndClassesLeft(alert.snoozedUntil);
    title = output.naturalDate;
    tooltipTitle = 'Snoozed Until';
    tooltipDescription = `${FormatDateTime(alert.snoozedUntil)}`;
    classNames.push(output.className);
  }

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
    setShowCustomDate(false);
    setIsSaving(false);
    form.reset();
  };

  const handleClick = () => {
    const dialog: IDialog = {
      id: `snoozeAlert${alert.id}`,
      name: 'AddressEvents',
      payload: {
        addressType: 'snooze',
        alert,
        patientId: alert.patientId,
        formData: null,
        onSuccess: (snoozedUntil: string) => {
          if (onSnoozed) onSnoozed(snoozedUntil);
        }
      }
    };
    if (form && form.getForm() && form.getForm().values) {
      dialog.payload.formData = form.getForm().values;
    }
    addDialog(dialog);
    handlePopoverClose();
  };

  const handleUnsnooze = () => {
    const params: UnsnoozeAlertParams = {
      ids: [alert.id]
    };
    Modal.confirm({
      title: 'Unsnooze Alert',
      content: <span>Are you sure you want to unsnooze this alert?</span>,
      onOk: () => {
        setIsSaving(true);
        /**
         * Prior to unsnoozing, save whether or not
         * this is a stun alert, so that we can
         * more easily decrease the stun count
         *
         * Stun = soon to be unsnoozed
         */
        const stunThreshold = moment.tz().add(24, 'hours');
        const stunAlertIds = [];
        if (moment(alert.snoozedUntil) < stunThreshold) {
          stunAlertIds.push(alert.id);
        }
        PostJSON<AlertsResponse, UnsnoozeAlertParams>({
          url: '/alert/unsnooze/',
          params
        }).then((response) => {
          setIsSaving(false);
          if (response.status !== 'OK') {
            Modal.error({
              title: 'Error Unsnoozing Alert',
              content: response.message
            });
            return;
          }
          // Unsnooze the alerts in the triage state
          if ('alerts' in response) {
            if (response.alerts) {
              handleToggleSnoozeAlert(response.alerts, true, stunAlertIds);
            }
          }
          handlePopoverClose();
        });
      }
    });
  };

  const openPopover = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    setIsPopoverOpen(true);
  };

  const submit = (data: SnoozeFormData) => {
    const params: SnoozeFormData = {
      reason: data.reason,
      note: data.note,
      misc: data.misc,
      tz: data.tz
    };
    // Snoozing one or more alerts
    if (!data.ids || !data.ids.length) {
      params.ids = null;
    } else {
      params.ids = data.ids;
    }
    if (!data.snoozeUntil) {
      // NOTE: Might be confusing, snoozing until 12:00 AM
      params.snoozeUntil = getSnoozeDateFromDuration(data.duration);
    } else {
      params.snoozeUntil = data.snoozeUntil;
    }
    setIsSaving(true);
    PostJSON<AlertsResponse, SnoozeFormData>({
      url: '/alert/snooze/',
      params
    }).then((response) => {
      setIsSaving(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Snoozing Alert',
          content: response.message
        });
        return;
      }
      // Add _justSnoozed to returned alerts
      if ('alerts' in response) {
        if (response.alerts) {
          const alerts = response.alerts.map((alert) => {
            return {
              ...alert,
              _justSnoozed: true
            };
          });
          handleToggleSnoozeAlert(alerts);
          const updated = alerts.find((a) => a.id === alert.id);
          if (updated && onSnoozed) {
            onSnoozed(
              updated.snoozedUntil instanceof Date
                ? updated.snoozedUntil.toISOString()
                : updated.snoozedUntil
            );
          }
        }
      }
      handlePopoverClose();
    });
  };

  const onChange = (data: SnoozeFormData) => {
    const prevData = formDataRef.current;
    if (prevData.snoozeUntil == null && data.snoozeUntil != null) {
      if (data.duration != null) {
        form.setData(
          produce(data, (draft) => {
            draft.duration = null;
          })
        );
      }
    } else if (prevData.duration == null && data.duration != null) {
      if (data.snoozeUntil != null) {
        form.setData(
          produce(data, (draft) => {
            draft.snoozeUntil = null;
          })
        );
      }
    }
    formDataRef.current = data;
  };

  /**
   * This popover content shows if the alert
   * has already been snoozed
   */
  const isSnoozedContent =
    (
      /**
       * The following event handler is only used to capture
       * click events before they bubble up to the parent.
       *
       * This prevents an issue where interacting with the
       * popover in an <Alert/> card triggers a patient
       * dialog to open.
       */
      <div
        className="pa-4 text-small quick-snooze-content"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-center font-bold">
          {tooltipTitle}
          <br />
          {tooltipDescription}
        </p>
        <Button
          className="mt-4 text-small"
          onClick={handleUnsnooze}
          intent="primary"
          size="small"
          fill
        >
          Unsnooze
        </Button>
        <AnchorButton
          className="mt-4 text-small hover-no-underline"
          onClick={handleClick}
          size="small"
          fill
        >
          Open Snooze Details
        </AnchorButton>
      </div>
    );

  /**
   * This popover content shows if the alert
   * hasn't already been snoozed
   */
  const quickSnoozeContent =
    (
      /**
       * The following event handler is only used to capture
       * click events before they bubble up to the parent.
       *
       * This prevents an issue where interacting with the
       * popover in an <Alert/> card triggers a patient
       * dialog to open.
       */
      <div
        className="pa-4 text-small quick-snooze-content"
        onClick={(event) => event.stopPropagation()}
      >
        <Form
          form={form}
          validate={validate}
          onSubmit={submit}
          onChange={onChange}
        >
          <RadioGroup
            label="Snooze Alert for"
            name="duration"
            options={getSnoozeDurationOptions(true)}
          />
          {showCustomDate ? (
            <DateInput
              label="Choose Date"
              name="snoozeUntil"
              min={moment().add(1, 'day').startOf('day').toDate()}
              fill
            />
          ) : (
            <AnchorButton
              className="mb-4 pl-0-important block text-small"
              onClick={() => setShowCustomDate(true)}
              intent="primary"
              bare
              variant="minimal"
              size="small"
            >
              Custom
            </AnchorButton>
          )}
          <RadioGroup
            label="Reason for Snoozing"
            name="reason"
            options={options.reason.map((opt: any) => {
              return {
                label: opt,
                value: opt
              };
            })}
          />
          <SubmitButton
            intent="primary"
            className="mb-0 text-small"
            disabled={isSaving}
            fill
            size="small"
          >
            <Icon icon={faMoon} /> Confirm Snooze
          </SubmitButton>
        </Form>
        <AnchorButton
          className="mt-4 text-small"
          onClick={handleClick}
          intent="primary"
          bare
          variant="minimal"
          size="small"
          fill
        >
          Open Snooze Details
        </AnchorButton>
      </div>
    );

  return (
    <Popover
      onClose={handlePopoverClose}
      isOpen={isPopoverOpen}
      content={IsAlertSnoozed(alert) ? isSnoozedContent : quickSnoozeContent}
      disabled={disabled}
    >
      <AnchorButton
        className={cc(classNames)}
        onClick={openPopover}
        variant="minimal"
        size="small"
        disabled={disabled}
      >
        <Icon icon={faMoon} title={title} />
      </AnchorButton>
    </Popover>
  );
};

/**
 * This action kicks off the snooze
 * patient flow
 */

interface SnoozePatientActionProps {
  patient: IPatient;
  compact?: boolean;
}

interface UnsnoozePatientParams {
  id: IPatient['id'];
}

export const SnoozePatientAction = ({
  patient,
  compact = false
}: SnoozePatientActionProps): JSX.Element => {
  const { terms } = useMetaData();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const addDialog = useActiveDialogsUpdater();
  const { handleToggleSnoozePatient } = useTriage();
  const { settings: accountSettings } = useMetaData();
  const options = accountSettings?.snoozeOptions;
  let title = 'Snooze';
  let tooltipTitle = `Snooze ${terms.patient.capitalized}`;
  let tooltipDescription = null;
  const classNames = [];

  const initialData: SnoozeFormData = {
    id: patient.id,
    duration: '15-minutes',
    snoozeUntil: null,
    reason: options.reason[0],
    note: '',
    tz: GetCurrentTZ(),
    misc: ''
  };
  const formDataRef = useRef(initialData);
  const form = new FormInstance<SnoozeFormDataRaw, SnoozeFormData>(
    deserializeSnoozeFormData(initialData),
    {
      serialize: serializeSnoozeFormData,
      deserialize: deserializeSnoozeFormData
    }
  );

  if (IsPatientSnoozed(patient)) {
    const output = FormatDaysAndClassesLeft(patient.snoozedUntil);
    title = output.naturalDate;
    tooltipTitle = 'Snoozed Until';
    tooltipDescription = `${FormatDateTime(patient.snoozedUntil)}`;
    classNames.push(output.className);
  }

  const handlePopoverClose = () => {
    setShowCustomDate(false);
    setIsSaving(false);
    form.reset();
    setIsPopoverOpen(false);
  };

  const handleClick = () => {
    const dialog: IDialog = {
      id: `snoozePatient${patient.id}`,
      name: 'AddressEvents',
      payload: {
        addressType: 'snooze',
        patientId: patient.id,
        formData: null
      }
    };
    if (form && form.getForm() && form.getForm().values) {
      dialog.payload.formData = form.getForm().values;
    }
    addDialog(dialog);
    handlePopoverClose();
  };

  const handleUnsnooze = () => {
    const params: UnsnoozePatientParams = {
      id: patient.id
    };
    Modal.confirm({
      title: 'Unsnooze Patient',
      content: (
        <span>
          Are you sure you want to unsnooze this patient? Their alerts will also
          be unsnoozed.
        </span>
      ),
      onOk: () => {
        setIsSaving(true);
        PostJSON<PatientResponse, UnsnoozePatientParams>({
          url: '/patient/unsnooze/',
          params
        }).then((response) => {
          setIsSaving(false);
          if (response.status !== 'OK') {
            Modal.error({
              title: 'Error Unsnoozing Patient',
              content: response.message
            });
            return;
          }
          // Unsnooze the patient in the triage state
          if ('patient' in response) {
            if (response.patient) {
              handleToggleSnoozePatient(
                response.patient,
                response.alerts,
                true
              );
            }
          }
          handlePopoverClose();
        });
      }
    });
  };

  const openPopover = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    setIsPopoverOpen(true);
  };

  const submit = (data: SnoozeFormData) => {
    const params: SnoozeFormData = {
      id: data.id,
      reason: data.reason,
      note: data.note,
      misc: data.misc,
      tz: data.tz
    };
    if (!data.snoozeUntil) {
      // NOTE: Might be confusing, snoozing until 12:00 AM
      params.snoozeUntil = getSnoozeDateFromDuration(data.duration);
    } else {
      params.snoozeUntil = data.snoozeUntil;
    }
    setIsSaving(true);
    PostJSON<PatientResponse, SnoozeFormData>({
      url: '/patient/snooze/',
      params
    }).then((response) => {
      setIsSaving(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Snoozing Patient',
          content: response.message
        });
        return;
      }
      // Add _justSnoozed to returned patient
      if ('patient' in response) {
        if (response.patient) {
          const patient = {
            ...response.patient,
            _justSnoozed: true
          };
          const alerts = [];
          if (response.alerts) {
            for (const alert of response.alerts) {
              alerts.push({
                ...alert,
                _justSnoozed: true
              });
            }
          }
          handleToggleSnoozePatient(patient, alerts);
        }
      }
      handlePopoverClose();
    });
  };

  const onChange = (data: SnoozeFormData) => {
    const prevData = formDataRef.current;
    if (prevData.snoozeUntil == null && data.snoozeUntil != null) {
      if (data.duration != null) {
        form.setData(
          produce(data, (draft) => {
            draft.duration = null;
          })
        );
      }
    } else if (prevData.duration == null && data.duration != null) {
      if (data.snoozeUntil != null) {
        form.setData(
          produce(data, (draft) => {
            draft.snoozeUntil = null;
          })
        );
      }
    }
    formDataRef.current = data;
  };

  /**
   * This popover content shows if the patient
   * has already been snoozed
   */
  const isSnoozedContent = (
    <div className="pa-4 text-small quick-snooze-content">
      <p className="text-center font-bold">
        {tooltipTitle}
        <br />
        {tooltipDescription}
      </p>
      <Button
        className="mt-4 text-small"
        onClick={handleUnsnooze}
        intent="primary"
        size="small"
        fill
      >
        Unsnooze
      </Button>
      <AnchorButton
        className="mt-4 text-small hover-no-underline"
        onClick={handleClick}
        size="small"
        fill
      >
        Open Snooze Details
      </AnchorButton>
    </div>
  );

  /**
   * This popover content shows if the patient
   * hasn't already been snoozed
   */
  const quickSnoozeContent = (
    <div className="pa-4 text-small quick-snooze-content">
      <Form
        form={form}
        validate={validate}
        onSubmit={submit}
        onChange={onChange}
      >
        <RadioGroup
          label={`Snooze ${terms.patient.capitalized} for`}
          name="duration"
          options={getSnoozeDurationOptions(true)}
        />
        {showCustomDate ? (
          <DateInput
            label="Choose Date"
            name="snoozeUntil"
            min={moment().add(1, 'day').startOf('day').toDate()}
            fill
          />
        ) : (
          <AnchorButton
            className="mb-4 pl-0-important block text-small"
            onClick={() => setShowCustomDate(true)}
            intent="primary"
            bare
            variant="minimal"
            size="small"
          >
            Custom
          </AnchorButton>
        )}
        <RadioGroup
          label="Reason for Snoozing"
          name="reason"
          options={options.reason.map((opt: any) => {
            return {
              label: opt,
              value: opt
            };
          })}
        />
        <SubmitButton
          intent="primary"
          className="mb-0 text-small"
          disabled={isSaving}
          fill
          size="small"
        >
          <Icon icon={faMoon} /> Confirm Snooze
        </SubmitButton>
      </Form>
      <AnchorButton
        className="mt-4 text-small"
        onClick={handleClick}
        intent="primary"
        bare
        variant="minimal"
        size="small"
        fill
      >
        Open Snooze Details
      </AnchorButton>
    </div>
  );

  return (
    <Popover
      onClose={handlePopoverClose}
      isOpen={isPopoverOpen}
      content={
        IsPatientSnoozed(patient) ? isSnoozedContent : quickSnoozeContent
      }
    >
      <Tooltip content={title} disabled={!compact || isPopoverOpen}>
        <AnchorButton
          className={cc(classNames)}
          onClick={openPopover}
          variant="minimal"
        >
          <Icon
            className={compact ? undefined : 'mr-2'}
            icon={faMoon}
            title={title}
          />
          {!compact && (
            <Text className="text-small" dotted>
              <span role="status">{title}</span>
            </Text>
          )}
        </AnchorButton>
      </Tooltip>
    </Popover>
  );
};
